import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Avatar,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Pagination,
  Badge,
  Card,
  CardContent,
  Tab,
  Tabs,
  Collapse
} from '@mui/material';
import {
  Store as StoreIcon,
  Campaign as CampaignIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Flag as FlagIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  ShoppingCart as ProductIcon,
  ReceiptLong as LedgerIcon,
  Summarize as SummaryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon,
  AccessTime as TimeIcon,
  ProductionQuantityLimits as NoProductIcon,
  ConfirmationNumber as VoucherIcon,
  CheckCircle as RedeemedIcon,
  Cancel as UnusedIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import {
  getAllVendorsReport,
  getVendorBusinessesProductsReport,
  getVendorPaymentSummaryReport,
  getVendorPaymentLedgerReport,
  getVendorVoucherTotals,
  approveVendorBusiness,
  disableVendorBusiness
} from '../../../api/analytics/analytics';

// Types
interface Vendor {
  vendor_id: string;
  vendor_name: string;
  email: string;
  vendor_phone: string;
}

interface Business {
  vbusiness_id: string;
  business_name: string;
  business_address: string;
  product_id: string | null;
  product_name: string | null;
  price: string | null;
  quantity: string | null;
  instocks: number | null;
  status?: string;
}

interface GroupedBusiness {
  vbusiness_id: string;
  business_name: string;
  business_address: string;
  products: Product[];
  status?: string;
}

interface Product {
  product_id: string;
  product_name: string;
  price: string;
  quantity: string;
  instocks: number;
}

interface CampaignPayment {
  campaign_id: string;
  campaign_name: string;
  start_date: string;
  end_date: string;
  total_vouchers: number;
  total_payments: number;
  used_vouchers?: number;
  remaining_vouchers?: number;
}

interface PaymentLedger {
  id: number;
  walletID: string;
  user_id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  shopID: string;
  drValue: string;
  crValue: string;
  amount: number;
  desp: string;
  dateCreated: string;
  last_updated: string;
}

interface PaymentSummary {
  total_payments: number;
  campaigns: CampaignPayment[];
}

interface VoucherTotals {
  total_vouchers: number;
  redeemed_vouchers: number;
  remaining_vouchers: number;
}

interface FilterOptions {
  search: string;
}

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vendor-tabpanel-${index}`}
      aria-labelledby={`vendor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Vendor Info Card Component
const VendorInfoCard = ({ vendor }: { vendor: Vendor | null }) => {
  const theme = useTheme();

  if (!vendor) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
            <StoreIcon />
          </Avatar>
          <Typography variant="h6" fontWeight={600}>
            Vendor Information
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderColor: alpha(theme.palette.primary.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Vendor Name
              </Typography>
              <Stack spacing={2}>
                <Box>

                  <Typography variant="body1" fontWeight={600}>
                    {vendor.vendor_name}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.02),
                borderColor: alpha(theme.palette.info.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <EmailIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Email
              </Typography>
              <Stack spacing={2}>
                <Box>

                  <Typography variant="body2">
                    {vendor.email}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.success.main, 0.02),
                borderColor: alpha(theme.palette.success.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PhoneIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Phone
              </Typography>
              <Stack spacing={2}>
                <Box>

                  <Typography variant="body1" fontWeight={600}>
                    {vendor.vendor_phone || 'No phone provided'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Business Card Component
const BusinessCard = ({ business, onRefresh }: { business: GroupedBusiness; onRefresh: () => void }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [approving, setApproving] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const formatAmount = (amount?: string) => {
    if (!amount) return '₦0.00';
    return `₦${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
              <BusinessIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={600}>
                  {business.business_name}
                </Typography>
                {business.status && (
                  <Chip
                    size="small"
                    label={business.status.toUpperCase()}
                    color={business.status.toLowerCase() === 'pending' ? 'error' : 'success'}
                    variant={business.status.toLowerCase() === 'pending' ? 'filled' : 'outlined'}
                  />
                )}
                {business.status?.toLowerCase() === 'pending' && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={approving}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setApproving(true);
                      try {
                        const res = await approveVendorBusiness(business.vbusiness_id, 'approved');
                        if (res?.status === true || res?.success || res) {
                          onRefresh();
                        }
                      } catch (err) {
                        console.error('Approval failed:', err);
                      } finally {
                        setApproving(false);
                      }
                    }}
                    sx={{ marginLeft: 'auto', height: 28, textTransform: 'none' }}
                  >
                    {approving ? <CircularProgress size={16} color="inherit" /> : 'Approve'}
                  </Button>
                )}
                {business.status?.toLowerCase() === 'approved' && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={disabling}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setDisabling(true);
                      try {
                        const res = await disableVendorBusiness(business.vbusiness_id);
                        if (res?.status === true || res?.success || res) {
                          onRefresh();
                        }
                      } catch (err) {
                        console.error('Disable failed:', err);
                      } finally {
                        setDisabling(false);
                      }
                    }}
                    sx={{ marginLeft: 'auto', height: 28, textTransform: 'none' }}
                  >
                    {disabling ? <CircularProgress size={16} color="inherit" /> : 'Disable'}
                  </Button>
                )}
              </Stack>
              {business.business_address && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 16 }} />
                  {business.business_address}
                </Typography>
              )}
              <Chip
                size="small"
                label={`${business.products.length} product${business.products.length !== 1 ? 's' : ''}`}
                sx={{ mt: 1, bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.dark }}
              />
            </Box>
          </Stack>
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expanded}>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ProductIcon fontSize="small" />
              Products
            </Typography>

            {business.products.length > 0 ? (
              <Grid container spacing={2}>
                {business.products.map((product) => (
                  <Grid item xs={12} sm={6} key={product.product_id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        borderColor: alpha(theme.palette.primary.main, 0.1),
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderColor: theme.palette.primary.main
                        }
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }}
                        >
                          <ProductIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {product.product_name}
                          </Typography>
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Price
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="success.main">
                                {formatAmount(product.price)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Stock
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {product.instocks || 0} units
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Quantity
                              </Typography>
                              <Typography variant="body2">
                                {product.quantity || 0}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.warning.main, 0.02),
                  borderColor: alpha(theme.palette.warning.main, 0.1)
                }}
              >
                <NoProductIcon sx={{ fontSize: 40, color: theme.palette.grey[400], mb: 1 }} />
                <Typography color="text.secondary">
                  No products found for this business
                </Typography>
              </Paper>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// Main Component
const VendorReport = () => {
  const theme = useTheme();
  const history = useHistory();

  // Data states
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  // Tab data states
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [groupedBusinesses, setGroupedBusinesses] = useState<GroupedBusiness[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [paymentLedger, setPaymentLedger] = useState<PaymentLedger[]>([]);
  const [voucherTotals, setVoucherTotals] = useState<VoucherTotals | null>(null);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);

  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    search: ''
  });

  // UI states
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState({
    vendors: false,
    businesses: false,
    payments: false,
    ledger: false,
    vouchers: false
  });
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch vendors on initial load
  useEffect(() => {
    fetchVendors();
  }, []);

  // Update selected vendor when vendors load or selection changes
  useEffect(() => {
    if (selectedVendorId && vendors.length > 0) {
      const vendor = vendors.find(v => v.vendor_id === selectedVendorId);
      setSelectedVendor(vendor || null);
    } else {
      setSelectedVendor(null);
    }
  }, [selectedVendorId, vendors]);

  // Fetch all vendor data when vendor is selected
  useEffect(() => {
    if (selectedVendorId) {
      fetchVendorBusinesses();
      fetchVendorPaymentSummary();
      fetchVendorPaymentLedger();
      fetchVendorVoucherTotals();
    } else {
      // Reset all data
      setBusinesses([]);
      setGroupedBusinesses([]);
      setPaymentSummary(null);
      setPaymentLedger([]);
      setVoucherTotals(null);
    }
  }, [selectedVendorId]);

  // Group businesses and their products when businesses data changes
  useEffect(() => {
    if (businesses.length > 0) {
      const grouped = groupBusinessesByVbusiness(businesses);
      setGroupedBusinesses(grouped);
    } else {
      setGroupedBusinesses([]);
    }
  }, [businesses]);

  const groupBusinessesByVbusiness = (businessList: Business[]): GroupedBusiness[] => {
    const businessMap = new Map<string, GroupedBusiness>();

    businessList.forEach(item => {
      if (!businessMap.has(item.vbusiness_id)) {
        businessMap.set(item.vbusiness_id, {
          vbusiness_id: item.vbusiness_id,
          business_name: item.business_name,
          business_address: item.business_address,
          status: item.status,
          products: []
        });
      }

      if (item.product_id && item.product_name) {
        const business = businessMap.get(item.vbusiness_id)!;
        business.products.push({
          product_id: item.product_id,
          product_name: item.product_name,
          price: item.price || '0',
          quantity: item.quantity || '0',
          instocks: item.instocks || 0
        });
      }
    });

    return Array.from(businessMap.values());
  };

  const fetchVendors = async () => {
    setLoading(prev => ({ ...prev, vendors: true }));
    try {
      const response = await getAllVendorsReport();
      const data = response?.data?.data || response?.data || response;
      const vendorsArray = Array.isArray(data) ? data : (data ? [data] : []);
      setVendors(vendorsArray);
      setError(null);

      if (vendorsArray.length === 0) {
        showNotification('No vendors found', 'info');
      }
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError("Failed to load vendors");
      showNotification("Failed to load vendors", 'error');
    } finally {
      setLoading(prev => ({ ...prev, vendors: false }));
    }
  };

  const fetchVendorBusinesses = async () => {
    if (!selectedVendorId) return;

    setLoading(prev => ({ ...prev, businesses: true }));
    try {
      const response = await getVendorBusinessesProductsReport(selectedVendorId);
      const data = response?.data?.data || response?.data || response;
      setBusinesses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      showNotification('Failed to load businesses', 'error');
    } finally {
      setLoading(prev => ({ ...prev, businesses: false }));
    }
  };

  const fetchVendorPaymentSummary = async () => {
    if (!selectedVendorId) return;

    setLoading(prev => ({ ...prev, payments: true }));
    try {
      const response = await getVendorPaymentSummaryReport(selectedVendorId);
      const data = response?.data?.data || response?.data || response;
      setPaymentSummary(data || null);
    } catch (err) {
      console.error('Error fetching payment summary:', err);
      showNotification('Failed to load payment summary', 'error');
    } finally {
      setLoading(prev => ({ ...prev, payments: false }));
    }
  };

  const fetchVendorPaymentLedger = async () => {
    if (!selectedVendorId) return;

    setLoading(prev => ({ ...prev, ledger: true }));
    try {
      const response = await getVendorPaymentLedgerReport(selectedVendorId);
      const data = response?.data?.data || response?.data || response;
      setPaymentLedger(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching payment ledger:', err);
      showNotification('Failed to load payment ledger', 'error');
    } finally {
      setLoading(prev => ({ ...prev, ledger: false }));
    }
  };

  const fetchVendorVoucherTotals = async () => {
    if (!selectedVendorId) return;

    setLoading(prev => ({ ...prev, vouchers: true }));
    try {
      const response = await getVendorVoucherTotals(selectedVendorId);
      const data = response?.data?.data || response?.data || response;
      setVoucherTotals(data || null);
    } catch (err) {
      console.error('Error fetching voucher totals:', err);
      showNotification('Failed to load voucher totals', 'error');
    } finally {
      setLoading(prev => ({ ...prev, vouchers: false }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVendors();
    if (selectedVendorId) {
      await fetchVendorBusinesses();
      await fetchVendorPaymentSummary();
      await fetchVendorPaymentLedger();
      await fetchVendorVoucherTotals();
    }
    setRefreshing(false);
    showNotification('Data refreshed successfully', 'success');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleCampaignExpand = (campaignId: string) => {
    setExpandedCampaign(expandedCampaign === campaignId ? null : campaignId);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatAmount = (amount?: number | string) => {
    if (amount === undefined || amount === null || amount === '') return '₦0.00';
    return `₦${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate stats
  const stats = {
    totalBusinesses: groupedBusinesses.length,
    totalProducts: groupedBusinesses.reduce((sum, b) => sum + b.products.length, 0),
    totalPayments: paymentSummary?.total_payments || 0,
    totalTransactions: paymentLedger.length,
    totalVouchers: voucherTotals?.total_vouchers || 0,
    totalRedeemed: voucherTotals?.redeemed_vouchers || 0,
    totalRemaining: voucherTotals?.remaining_vouchers || 0
  };

  // Calculate redemption percentage
  const redemptionPercentage = stats.totalVouchers > 0
    ? ((stats.totalRedeemed / stats.totalVouchers) * 100).toFixed(1)
    : '0';

  // Pagination for vendors table (if needed)
  const filteredVendors = vendors.filter(v => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return v.vendor_name?.toLowerCase().includes(searchLower) ||
      v.email?.toLowerCase().includes(searchLower) ||
      v.vendor_phone?.includes(searchLower);
  });

  const pageCount = Math.ceil(filteredVendors.length / rowsPerPage);
  const paginatedVendors = filteredVendors.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const activeFilterCount = filters.search ? 1 : 0;

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()} edge="start">
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <StoreIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Vendor Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View vendor businesses, products and payment details
                  </Typography>
                </Box>
                <Tooltip title="Refresh">
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="xl" sx={{ py: 3 }}>

            {/* Vendor Selection */}
            <Paper sx={{ mb: 3, p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Vendor</InputLabel>
                    <Select
                      value={selectedVendorId}
                      label="Select Vendor"
                      onChange={(e) => setSelectedVendorId(e.target.value)}
                      disabled={loading.vendors}
                    >
                      <MenuItem value="">
                        <em>Select a vendor</em>
                      </MenuItem>
                      {vendors.map((vendor) => (
                        <MenuItem key={vendor.vendor_id} value={vendor.vendor_id}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                            <StoreIcon fontSize="small" color="primary" />
                            <Typography sx={{ flex: 1 }}>{vendor.vendor_name}</Typography>
                            <Chip
                              size="small"
                              label={vendor.email}
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {selectedVendorId && (
              <>
                {/* Vendor Info */}
                {selectedVendor && <VendorInfoCard vendor={selectedVendor} />}

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                            <BusinessIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Businesses
                            </Typography>
                            <Typography variant="h4">
                              {stats.totalBusinesses}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <ProductIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Products
                            </Typography>
                            <Typography variant="h4">
                              {stats.totalProducts}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <VoucherIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Vouchers
                            </Typography>
                            <Typography variant="h4">
                              {loading.vouchers ? <CircularProgress size={24} /> : formatNumber(stats.totalVouchers)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                            <RedeemedIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Redeemed
                            </Typography>
                            <Typography variant="h4" color="success.main">
                              {loading.vouchers ? <CircularProgress size={24} /> : formatNumber(stats.totalRedeemed)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                            <UnusedIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Remaining
                            </Typography>
                            <Typography variant="h4" color="error.main">
                              {loading.vouchers ? <CircularProgress size={24} /> : formatNumber(stats.totalRemaining)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Tabs */}
                <Paper sx={{ mb: 3 }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                      <Tab
                        icon={<BusinessIcon />}
                        label="Businesses & Products"
                        iconPosition="start"
                      />
                      <Tab
                        icon={<SummaryIcon />}
                        label="Voucher Details"
                        iconPosition="start"
                      />
                      <Tab
                        icon={<LedgerIcon />}
                        label="Payment Ledger"
                        iconPosition="start"
                      />
                    </Tabs>
                  </Box>

                  {/* Businesses & Products Tab */}
                  <TabPanel value={tabValue} index={0}>
                    {loading.businesses ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : groupedBusinesses.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <BusinessIcon sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
                        <Typography color="text.secondary">
                          No businesses found for this vendor
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        {groupedBusinesses.map((business) => (
                          <BusinessCard
                            key={business.vbusiness_id}
                            business={business}
                            onRefresh={fetchVendorBusinesses}
                          />
                        ))}
                      </Box>
                    )}
                  </TabPanel>

                  {/* Voucher Details Tab */}
                  <TabPanel value={tabValue} index={1}>
                    {loading.payments ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : !paymentSummary || paymentSummary.campaigns?.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <VoucherIcon sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
                        <Typography color="text.secondary">
                          No voucher records found
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Voucher Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                    <VoucherIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Vouchers
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700}>
                                      {formatNumber(stats.totalVouchers)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                                    <RedeemedIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Redeemed Vouchers
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} color="success.main">
                                      {formatNumber(stats.totalRedeemed)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                                    <UnusedIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Unused Vouchers
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} color="warning.main">
                                      {formatNumber(stats.totalRemaining)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Redemption Rate Card */}
                        <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                          <CardContent>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                                  <SummaryIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Overall Redemption Rate
                                  </Typography>
                                  <Typography variant="h3" fontWeight={700} color="primary.main">
                                    {redemptionPercentage}%
                                  </Typography>
                                </Box>
                              </Stack>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {formatNumber(stats.totalRedeemed)} redeemed out of {formatNumber(stats.totalVouchers)} total
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>

                        {/* Campaign-wise Summary */}
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                          Campaign-wise Breakdown
                        </Typography>

                        {paymentSummary.campaigns?.map((campaign) => {
                          // Calculate campaign-specific redemption percentage
                          const campaignRedemptionPercentage = campaign.total_vouchers > 0
                            ? ((campaign.used_vouchers || 0) / campaign.total_vouchers * 100).toFixed(1)
                            : '0';

                          return (
                            <Card key={campaign.campaign_id} variant="outlined" sx={{ mb: 2 }}>
                              <CardContent>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() => toggleCampaignExpand(campaign.campaign_id)}
                                >
                                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                      <CampaignIcon />
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle1" fontWeight={600}>
                                        {campaign.campaign_name}
                                      </Typography>
                                      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <DateRangeIcon sx={{ fontSize: 14 }} />
                                          {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                  </Stack>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{ textAlign: 'right' }}>
                                      <Typography variant="body2" fontWeight={600} color="primary.main">
                                        {campaignRedemptionPercentage}% Redeemed
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {campaign.total_vouchers} total vouchers
                                      </Typography>
                                    </Box>
                                    {expandedCampaign === campaign.campaign_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </Stack>
                                </Stack>

                                <Collapse in={expandedCampaign === campaign.campaign_id}>
                                  <Box sx={{ mt: 3, pl: { xs: 0, sm: 7 } }}>
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Metric</TableCell>
                                            <TableCell align="right">Value</TableCell>
                                            <TableCell align="right">Percentage</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          <TableRow>
                                            <TableCell>Total Vouchers</TableCell>
                                            <TableCell align="right">{campaign.total_vouchers}</TableCell>
                                            <TableCell align="right">100%</TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell>Redeemed Vouchers</TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                                              {campaign.used_vouchers || 0}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main' }}>
                                              {campaign.total_vouchers > 0
                                                ? `${((campaign.used_vouchers || 0) / campaign.total_vouchers * 100).toFixed(1)}%`
                                                : '0%'}
                                            </TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell>Unused Vouchers</TableCell>
                                            <TableCell align="right" sx={{ color: 'warning.main' }}>
                                              {campaign.remaining_vouchers || 0}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'warning.main' }}>
                                              {campaign.total_vouchers > 0
                                                ? `${((campaign.remaining_vouchers || 0) / campaign.total_vouchers * 100).toFixed(1)}%`
                                                : '0%'}
                                            </TableCell>
                                          </TableRow>
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                </Collapse>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </>
                    )}
                  </TabPanel>

                  {/* Payment Ledger Tab */}
                  <TabPanel value={tabValue} index={2}>
                    {loading.ledger ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : paymentLedger.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <LedgerIcon sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
                        <Typography color="text.secondary">
                          No ledger entries found
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                                    <AccountBalanceIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total DR (Credit)
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="success.main">
                                      {formatAmount(paymentLedger.reduce((sum, entry) => {
                                        const drValue = parseFloat(entry.drValue) || 0;
                                        return sum + drValue;
                                      }, 0))}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                                    <PaymentIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total CR (Debit)
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="error.main">
                                      {formatAmount(paymentLedger.reduce((sum, entry) => {
                                        const crValue = parseFloat(entry.crValue) || 0;
                                        return sum + crValue;
                                      }, 0))}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                    <AccountBalanceIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Net Amount
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="info.main">
                                      {formatAmount(paymentLedger.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0))}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                                    <ReceiptIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Transactions
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                      {paymentLedger.length}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Ledger Table */}
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Wallet ID</TableCell>
                                <TableCell>Campaign</TableCell>
                                <TableCell>Shop ID</TableCell>
                                <TableCell align="right">DR (Credit)</TableCell>
                                <TableCell align="right">CR (Debit)</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paymentLedger.map((entry) => {
                                const drValue = parseFloat(entry.drValue) || 0;
                                const crValue = parseFloat(entry.crValue) || 0;

                                return (
                                  <TableRow key={entry.id} hover>
                                    <TableCell>
                                      <Tooltip title={formatDateTime(entry.dateCreated)}>
                                        <Typography variant="body2">
                                          {formatDate(entry.dateCreated)}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: 250,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {entry.desp || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                                        {entry.walletID}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {entry.campaign_name || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {entry.shopID || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {drValue > 0 ? (
                                        <Typography variant="body2" fontWeight={600} color="success.main">
                                          {formatAmount(drValue)}
                                        </Typography>
                                      ) : (
                                        <Typography variant="body2" color="text.disabled">
                                          -
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {crValue > 0 ? (
                                        <Typography variant="body2" fontWeight={600} color="error.main">
                                          {formatAmount(crValue)}
                                        </Typography>
                                      ) : (
                                        <Typography variant="body2" color="text.disabled">
                                          -
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color={entry.amount > 0 ? 'success.main' : entry.amount < 0 ? 'error.main' : 'text.primary'}
                                      >
                                        {formatAmount(entry.amount)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Totals Row */}
                              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableCell colSpan={5}>
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    TOTALS
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700} color="success.main">
                                    {formatAmount(paymentLedger.reduce((sum, entry) => {
                                      const drValue = parseFloat(entry.drValue) || 0;
                                      return sum + drValue;
                                    }, 0))}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700} color="error.main">
                                    {formatAmount(paymentLedger.reduce((sum, entry) => {
                                      const crValue = parseFloat(entry.crValue) || 0;
                                      return sum + crValue;
                                    }, 0))}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700} color="info.main">
                                    {formatAmount(paymentLedger.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0))}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </TabPanel>
                </Paper>
              </>
            )}

            {/* No Vendor Selected State */}
            {!selectedVendorId && !loading.vendors && (
              <Paper sx={{ py: 8, textAlign: 'center' }}>
                <Stack spacing={2} alignItems="center">
                  <Avatar sx={{ width: 80, height: 80, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <StoreIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Select a Vendor
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a vendor from the dropdown above to view their details
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default VendorReport;